import { useCallback, useEffect, useRef, useState } from "react";

import {
  createPreviewMessage,
  isPreviewMessage,
} from "@/src/utils/appearancePreview";

import type { RefObject } from "react";

import type {
  AppearancePreviewState,
  AppearancePreviewStatus,
} from "@/src/types/appearancePreview.types";

// Poignée de main et envoi d'état : documentation/appearance-preview-contract.md (§4, §6).

export const APPEARANCE_PREVIEW_READY_TIMEOUT_MS = 10_000;

interface UseAppearancePreviewParams {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  state: AppearancePreviewState | null;
}

export interface UseAppearancePreviewReturn {
  status: AppearancePreviewStatus;
  handleLoad: () => void;
  reload: () => void;
}

function postPreviewMessage(
  iframe: HTMLIFrameElement | null,
  origin: string,
  type: string,
  payload: unknown,
): void {
  iframe?.contentWindow?.postMessage(
    createPreviewMessage(type, payload),
    origin,
  );
}

export function useAppearancePreview({
  iframeRef,
  state,
}: UseAppearancePreviewParams): UseAppearancePreviewReturn {
  const [status, setStatus] = useState<AppearancePreviewStatus>("connecting");
  const isReadyRef = useRef(false);
  const stateRef = useRef(state);
  const lastSentRef = useRef<string | null>(null);
  const targetOriginRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReadyTimeout = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const awaitReady = useCallback((): void => {
    isReadyRef.current = false;
    targetOriginRef.current = null;
    // Le nouveau document n'a rien reçu : le prochain envoi ne doit pas être dédupliqué.
    lastSentRef.current = null;
    clearReadyTimeout();
    setStatus("connecting");
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setStatus("unavailable");
    }, APPEARANCE_PREVIEW_READY_TIMEOUT_MS);
  }, [clearReadyTimeout]);

  const sendState = useCallback((): void => {
    const currentState = stateRef.current;
    const targetOrigin = targetOriginRef.current;
    if (!currentState || !targetOrigin) return;

    const serializedState = JSON.stringify(currentState);
    if (serializedState === lastSentRef.current) return;

    lastSentRef.current = serializedState;
    postPreviewMessage(iframeRef.current, targetOrigin, "state", currentState);
  }, [iframeRef]);

  const handleLoad = useCallback((): void => {
    awaitReady();
    // `hello` ne porte aucune donnée : la cible réelle est l'origine du `ready` (le storefront peut rediriger).
    postPreviewMessage(iframeRef.current, "*", "hello", {});
  }, [awaitReady, iframeRef]);

  useEffect(() => clearReadyTimeout, [clearReadyTimeout]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<unknown>): void => {
      if (
        event.source !== iframeRef.current?.contentWindow ||
        !isPreviewMessage(event.data, "ready")
      ) {
        return;
      }

      clearReadyTimeout();
      targetOriginRef.current = event.origin;
      isReadyRef.current = true;
      lastSentRef.current = null;
      setStatus("ready");
      sendState();
    };

    window.addEventListener("message", handleMessage);

    return (): void => {
      window.removeEventListener("message", handleMessage);
    };
  }, [clearReadyTimeout, iframeRef, sendState]);

  useEffect(() => {
    stateRef.current = state;
    if (isReadyRef.current) sendState();
  }, [sendState, state]);

  const reload = useCallback((): void => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Réassigner le même `src` recharge l'iframe, y compris cross-origin.
    // eslint-disable-next-line no-self-assign
    iframe.src = iframe.src;
    // Le `hello` partira au `load` du nouveau document, pas vers l'ancien.
    awaitReady();
  }, [awaitReady, iframeRef]);

  return { status, handleLoad, reload };
}
